import React, { useState, useEffect } from 'react';

import {
  Row,
  Col,
  Button,
  Select,
  Typography,
  Switch,
  Divider,
  message,
  Tag
} from 'antd';

import { get, post, put } from '../../utils/httpRequests';
import { APIs } from '../../utils/apis';

import { timezones } from '../../utils/timezones';

const { Option } = Select;
const { Title, Paragraph, Text } = Typography;

const columns = [
  {
    title: 'Enterprise',
    key: 'report_name',
    width: 40
  },
  {
    title: 'Time Zone',
    key: 'report_summary',
    width: 30
  },
  {
    title: 'Status',
    key: 'report_active',
    width: 15
  },
  {
    title: 'Action',
    key: 'report_active',
    width: 15
  }
];

const TimezoneSetting = () => {
  const [state, setState] = useState({
    isFetching: true,
    enterpriseId: null,
    record: {},
    enterprises: [],
    list: [],
    checked: false
  });

  const fetchData = () => {
    Promise.all([
      get({
        url: APIs.ACTIVE_REPORTS.META_DATA + 'filters=enterprise'
      }),
      get({ url: APIs.SET_REPORT_TZ })
    ]).then(res => {
      if (res[0]?.status === 200) {
        state.enterprises =
          res[0]?.data?.result?.enterprises?.sort((a, b) =>
            a?.name?.localeCompare(b.name)
          ) ?? [];
        state.isFetching = false;
        setState({ ...state });
      }
      if (res[1]?.status === 200) {
        state.list = res[1]?.data?.result ?? [];
      }
      setState({ ...state, isFetching: false });
    });
  };

  const fetchEnterpriseTZData = enterpriseId => {
    setState({ ...state, isFetching: true });
    get({
      url: `${APIs.SET_REPORT_TZ}enterprise_id=${enterpriseId}`
    }).then(res => {
      if (
        res?.data?.meta?.status === 200 &&
        res?.data?.result?.length > 0 &&
        res?.data?.result[0] !== null
      ) {
        state.record = res.data.result[0];
        state.checked = res.data.result[0]?.is_active;
      } else {
        state.record = {};
      }
      state.enterpriseId = enterpriseId;
      setState({ ...state, isFetching: false });
    });
  };

  const onSubmit = () => {
    let request = null;
    const url = `${APIs.SET_REPORT_TZ}enterprise_id=${state.enterpriseId}`;
    const data = { ...state.record };
    let entryExists = false;
    if (data?.id) {
      delete data.id;
      entryExists = true;
      request = put({ url, data });
    } else {
      request = post({ url, data });
    }
    request
      .then(response => {
        if (!response) {
          throw Error(response.statusText);
        }
        if (response?.data?.meta?.success) {
          message.success(
            `Timezone entry ${entryExists ? 'updated' : 'created'} successfully`
          );
          fetchEnterpriseTZData(state.enterpriseId);
        }
      })
      .catch(function(error) {
        message.error(
          error
            ? JSON.stringify(error)
            : 'Service error: Please contact Frontend team'
        );
      });
  };

  const handleChange = (key, value) => {
    if (key === 'enterpriseId' && value) {
      fetchEnterpriseTZData(value);
    }
    if (key === 'timezone') {
      const tz = timezones.find(tz => tz.value === value);
      state.record.hours = tz?.hours;
      state.record.minutes = tz?.minutes;
      state.record.operation = tz?.operation;
      state.record.timezone_key = tz?.value;
    }
    if (key === 'checked') {
      state.record.is_active = value;
    }
    setState({ ...state, [key]: value });
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <main>
      <Typography className="margin-bottom-20">
        <Title>Timezone Settings - Track Orders Report</Title>
        <Paragraph>
          When an <Text strong>Enterprise</Text> wants to get the{' '}
          <Text strong>created_at</Text> and <Text strong>updated_at</Text> in a
          particular timezone, we configure the same in the system, and all
          Track-Order reports are generated with this timezone added for
          created_at and updated_at columns.
        </Paragraph>
      </Typography>
      <Row className="view-header">
        <Col span={3}>
          <span>Enterprise:</span>
        </Col>
        <Col span={21}>
          <div
            className="global-search-wrapper margin-bottom-20"
            style={{ width: '100%' }}
          >
            <Select
              showSearch
              style={{ width: '96%' }}
              placeholder="Select enterprise"
              optionFilterProp="children"
              value={state.enterpriseId ? state.enterpriseId : undefined}
              onChange={value => handleChange('enterpriseId', value)}
              filterOption={(input, option) =>
                option.props.children
                  ?.toLowerCase()
                  ?.indexOf(input?.toLowerCase()) >= 0
              }
            >
              {state.enterprises.map((enterprise, i) => (
                <Option key={i} value={enterprise.id}>
                  {enterprise.name}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
      </Row>
      {state.enterpriseId && (
        <Row className="view-header">
          <Col span={3}>
            <Tag color={state.record?.id ? 'green' : 'gray'}>
              {state.record?.id ? 'Entry Exists' : 'Entry not found'}
            </Tag>
          </Col>
        </Row>
      )}
      <Divider />
      <Row className="view-header">
        <Col span={3}>
          <span>Time Zone:</span>
        </Col>
        <Col span={21}>
          <div
            className="global-search-wrapper margin-bottom-20"
            style={{ width: '100%' }}
          >
            <Select
              showSearch
              style={{ width: '96%' }}
              placeholder="Select timezone"
              optionFilterProp="children"
              value={
                state.record?.timezone_key
                  ? state.record.timezone_key
                  : undefined
              }
              onChange={value => handleChange('timezone', value)}
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              disabled={!state.enterpriseId}
            >
              {timezones.map(item => (
                <Option key={item.value} value={item.value}>
                  {item.value}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
      </Row>
      <Row className="view-header">
        <Col span={3}>
          <span>Status:</span>
        </Col>
        <Col span={21} className="margin-bottom-20">
          <Switch
            checked={state.record?.is_active}
            onChange={value => handleChange('checked', value)}
            disabled={!state.record?.timezone_key}
          />
        </Col>
      </Row>
      <Row className="margin-bottom-10 view-header">
        <Col span="2" className="margin-bottom-20">
          <Button type="primary" onClick={onSubmit}>
            Save
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={24} className="margin-bottom-10">
          <h1 className="margin-top-10">
            Enterprises who have set Time Zone configurations
          </h1>
          {state.list.length <= 0 ? (
            <div>&nbsp; No Data</div>
          ) : (
            <>
              <table className={`ant-table ant-table-small width-100pt`}>
                <thead className="ant-table-thead">
                  <tr>
                    {columns.map((item, i) => (
                      <th
                        className="padding-8"
                        key={i}
                        width={`${item.width}%`}
                      >
                        {item.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="ant-table-tbody">
                  {state.list.map((record, i) => (
                    <tr key={i}>
                      <td className="padding-8">
                        {record.enterprise_user_id__user_id_id__username}
                      </td>
                      <td className="padding-8" style={{ maxWidth: '400px' }}>
                        {record.timezone_key}
                      </td>
                      <td className="padding-8">
                        {record.is_active ? 'Active' : 'Inactive'}
                      </td>
                      <td className="padding-8">
                        <Button
                          type="ghost"
                          onClick={() =>
                            setState({
                              ...state,
                              record,
                              enterpriseId: record.enterprise_user_id
                            })
                          }
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Col>
      </Row>
    </main>
  );
};
export default TimezoneSetting;
